import type { MerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  canReadSetting,
  getSettingCatalogDefinitionsForGroup,
  SettingCatalogGroups,
  type SettingDefinition,
  type SettingGroupDefinition,
  type SettingVisibility,
} from "@redotech/redo-model/settings-catalog/index";
import {
  searchSettingsCatalog,
  type SettingsSearchGroupResult,
} from "@redotech/redo-model/settings-catalog/search";
import {
  getSettingSearchEvidence,
  type SettingItem,
  type SettingItemSearchMatch,
  type SettingSearchEvidence,
  type SettingsSection,
  settingsSectionToFriendly,
} from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { type CommandPaletteItem } from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import Fuse from "fuse.js";
import { getSettingsGroupFullPageHref } from "./settings-agent/settings-group-full-page-links";

export type SettingsNavigationSearchRoutes = Pick<
  MerchantRoutes,
  "getSettingsSubRoute" | "legacySupportBaseUrl" | "settingsHref"
>;

interface AccessibleSettingsNavigationItem {
  canonicalHref: string;
  item: SettingItem;
  sectionName: string;
}

export interface SettingsNavigationCommandPaletteItem extends CommandPaletteItem {
  dedupeHrefs: readonly string[];
  href: string;
}

interface SettingsNavigationSearchCandidate {
  catalogScore: number;
  groupDescription: string;
  groupSearchTerms: readonly string[];
  groupTitle: string;
  item: SettingsNavigationCommandPaletteItem;
  matchedSettingDescriptions: readonly string[];
  matchedSettingLabels: readonly string[];
  matchedSettingSearchTerms: readonly string[];
  pageFriendlyName: string;
  pageTitle: string;
  routeSearchTerms: readonly string[];
  searchEvidence?: SettingSearchEvidence;
}

const DEFAULT_VIEWER_VISIBILITY: SettingVisibility = "merchant";
const DEFAULT_SETTINGS_NAVIGATION_SEARCH_LIMIT = 5;
const MAX_MATCHED_SETTING_LABELS = 3;
const SETTINGS_NAVIGATION_SEARCH_STOP_WORDS = new Set([
  "page",
  "setting",
  "settings",
]);
const VALUE_SEMANTICS_OPTION_SEARCH_STOP_WORDS = new Set([
  "custom",
  "gmail",
  "outlook",
]);
const SETTINGS_NAVIGATION_SEARCH_KEYS = [
  { name: "pageFriendlyName", weight: 8 },
  { name: "pageTitle", weight: 6 },
  { name: "matchedSettingLabels", weight: 5 },
  { name: "groupTitle", weight: 4 },
  { name: "matchedSettingSearchTerms", weight: 3 },
  { name: "groupSearchTerms", weight: 2 },
  { name: "groupDescription", weight: 1 },
  { name: "matchedSettingDescriptions", weight: 1 },
  { name: "routeSearchTerms", weight: 1 },
] as const;

export function addSettingsCatalogSearchTerms({
  routes,
  sections,
  viewerVisibility = DEFAULT_VIEWER_VISIBILITY,
}: {
  routes: SettingsNavigationSearchRoutes;
  sections: readonly SettingsSection[];
  viewerVisibility?: SettingVisibility;
}): SettingsSection[] {
  const accessibleItems = getAccessibleSettingsNavigationItems(
    sections,
    routes,
  );
  const itemSearchHints = new Map<SettingItem, Set<string>>();
  const itemSearchMatches = new Map<SettingItem, SettingItemSearchMatch[]>();
  const itemSearchTerms = new Map<SettingItem, Set<string>>();

  for (const group of SettingCatalogGroups) {
    const definitions = getVisibleDefinitionsForGroup({
      groupId: group.id,
      viewerVisibility,
    });
    if (
      definitions.length === 0 ||
      !canReadSetting(viewerVisibility, group.access)
    ) {
      continue;
    }

    const href = getSettingsGroupFullPageHref({ groupId: group.id, routes });
    const accessibleItem = findAccessibleSettingsNavigationItem({
      accessibleItems,
      href,
      routes,
    });
    if (!accessibleItem) {
      continue;
    }

    const terms = getCatalogSearchTerms({ definitions, group, href });
    const hints = getCatalogSearchHints({ definitions, group });
    const matches = getCatalogSearchMatches({ definitions, href });
    const targetHints = getOrCreateSearchValues(
      itemSearchHints,
      accessibleItem.item,
    );
    for (const hint of hints) {
      addSearchValue(targetHints, hint);
    }

    const targetMatches = getOrCreateSearchMatches(
      itemSearchMatches,
      accessibleItem.item,
    );
    for (const match of matches) {
      addSearchMatch(targetMatches, match);
    }

    const targetTerms = getOrCreateSearchTerms(
      itemSearchTerms,
      accessibleItem.item,
    );
    for (const term of terms) {
      addSearchValue(targetTerms, term);
    }
  }

  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => {
      const hints = itemSearchHints.get(item);
      const matches = itemSearchMatches.get(item);
      const terms = itemSearchTerms.get(item);
      if (
        !hints?.size &&
        !matches?.length &&
        !terms?.size &&
        !item.searchHints?.length &&
        !item.searchMatches?.length &&
        !item.searchTerms?.length
      ) {
        return item;
      }

      const mergedHints = new Set<string>();
      for (const hint of item.searchHints ?? []) {
        addSearchValue(mergedHints, hint);
      }
      for (const hint of hints ?? []) {
        addSearchValue(mergedHints, hint);
      }

      const mergedMatches = [...(item.searchMatches ?? [])];
      for (const match of matches ?? []) {
        addSearchMatch(mergedMatches, match);
      }

      const mergedTerms = new Set<string>();
      for (const term of item.searchTerms ?? []) {
        addSearchValue(mergedTerms, term);
      }
      for (const term of terms ?? []) {
        addSearchValue(mergedTerms, term);
      }

      return {
        ...item,
        searchHints: [...mergedHints],
        searchMatches: mergedMatches,
        searchTerms: [...mergedTerms],
      };
    }),
  }));
}

export function searchSettingsNavigationItems({
  limit = DEFAULT_SETTINGS_NAVIGATION_SEARCH_LIMIT,
  navigate,
  query,
  routes,
  sections,
  viewerVisibility = DEFAULT_VIEWER_VISIBILITY,
}: {
  limit?: number;
  navigate: (href: string) => void;
  query: string;
  routes: SettingsNavigationSearchRoutes;
  sections: readonly SettingsSection[];
  viewerVisibility?: SettingVisibility;
}): SettingsNavigationCommandPaletteItem[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery || sections.length === 0) {
    return [];
  }

  const accessibleItems = getAccessibleSettingsNavigationItems(
    sections,
    routes,
  );
  const seenHrefs = new Set<string>();

  const candidates = searchSettingsCatalog({
    query: trimmedQuery,
    limit: Math.max(limit * 4, 20),
    viewerVisibility,
  }).groups.flatMap((groupResult) => {
    const href = getSettingsGroupFullPageHref({
      groupId: groupResult.id,
      routes,
    });
    const accessibleItem = findAccessibleSettingsNavigationItem({
      accessibleItems,
      href,
      routes,
    });
    if (!accessibleItem) {
      return [];
    }

    if (seenHrefs.has(href)) {
      return [];
    }
    seenHrefs.add(href);

    return [
      createSettingsNavigationSearchCandidate({
        accessibleItem,
        groupResult,
        href,
        navigate,
        routes,
        viewerVisibility,
      }),
    ];
  });

  return searchSettingsNavigationCandidates({ candidates, query: trimmedQuery })
    .slice(0, limit)
    .map((candidate) => candidate.item);
}

export function removeSettingsNavigationDuplicatePageResults<
  TItem extends CommandPaletteItem,
>({
  pageResults,
  settingsResults,
}: {
  pageResults: readonly TItem[];
  settingsResults: readonly SettingsNavigationCommandPaletteItem[];
}): TItem[] {
  const settingsHrefs = new Set(
    settingsResults.flatMap((result) =>
      result.dedupeHrefs.map(canonicalizeSettingsNavigationHref),
    ),
  );

  return pageResults.filter((pageResult) => {
    return !settingsHrefs.has(
      canonicalizeSettingsNavigationHref(pageResult.id),
    );
  });
}

export function canonicalizeSettingsNavigationHref(href: string): string {
  try {
    const url = new URL(href, "https://redo.local");
    return normalizePathname(url.pathname);
  } catch {
    const withoutHash = href.split("#")[0] ?? "";
    const withoutQuery = withoutHash.split("?")[0] ?? "";
    return normalizePathname(withoutQuery);
  }
}

function settingsSearchGroupToCommandPaletteItem({
  accessibleItem,
  groupResult,
  href,
  navigate,
  routes,
}: {
  accessibleItem: AccessibleSettingsNavigationItem;
  groupResult: SettingsSearchGroupResult;
  href: string;
  navigate: (href: string) => void;
  routes: SettingsNavigationSearchRoutes;
}): SettingsNavigationCommandPaletteItem {
  const title = [
    "Settings",
    accessibleItem.sectionName,
    accessibleItem.item.friendlyName,
  ].join(" > ");
  const matchedSettingHref = groupResult.matchedSettings[0]?.id
    ? getSettingMatchHref({
        href,
        settingId: groupResult.matchedSettings[0].id,
      })
    : href;

  return {
    id: matchedSettingHref,
    href: matchedSettingHref,
    dedupeHrefs: getUniqueStrings([
      href,
      accessibleItem.item.href,
      ...getCanonicalRouteCandidates(href, routes),
    ]),
    title,
    subtitle: getMatchedSettingsSubtitle(groupResult),
    category: "pages",
    group: "Settings",
    action: ({ newTab }) => {
      if (newTab) {
        window.open(matchedSettingHref, "_blank");
      } else {
        navigate(matchedSettingHref);
      }
    },
  };
}

function getMatchedSettingsSubtitle(
  groupResult: SettingsSearchGroupResult,
): string {
  const matchedLabels = groupResult.matchedSettings
    .map((setting) => setting.label)
    .slice(0, MAX_MATCHED_SETTING_LABELS);

  if (matchedLabels.length > 0) {
    const prefix =
      matchedLabels.length === 1 ? "Matched setting" : "Matched settings";
    return `${prefix}: ${matchedLabels.join(", ")}`;
  }

  return groupResult.description;
}

function createSettingsNavigationSearchCandidate({
  accessibleItem,
  groupResult,
  href,
  navigate,
  routes,
  viewerVisibility,
}: {
  accessibleItem: AccessibleSettingsNavigationItem;
  groupResult: SettingsSearchGroupResult;
  href: string;
  navigate: (href: string) => void;
  routes: SettingsNavigationSearchRoutes;
  viewerVisibility: SettingVisibility;
}): SettingsNavigationSearchCandidate {
  const pageTitle = [
    "Settings",
    accessibleItem.sectionName,
    accessibleItem.item.friendlyName,
  ].join(" ");
  const group = SettingCatalogGroups.find(
    (candidateGroup) => candidateGroup.id === groupResult.id,
  );
  const definitions = getVisibleDefinitionsForGroup({
    groupId: groupResult.id,
    viewerVisibility,
  });
  const matchedSettingIds = new Set(
    groupResult.matchedSettings.map((setting) => setting.id),
  );
  const matchedDefinitions = definitions.filter((definition) =>
    matchedSettingIds.has(definition.id),
  );

  return {
    catalogScore: groupResult.score,
    groupDescription: groupResult.description,
    groupSearchTerms: getGroupVisibleSearchTerms(group),
    groupTitle: groupResult.title,
    item: settingsSearchGroupToCommandPaletteItem({
      accessibleItem,
      groupResult,
      href,
      navigate,
      routes,
    }),
    matchedSettingDescriptions: groupResult.matchedSettings.map(
      (setting) => setting.description,
    ),
    matchedSettingLabels: groupResult.matchedSettings.map(
      (setting) => setting.label,
    ),
    matchedSettingSearchTerms: matchedDefinitions.flatMap(
      getCatalogDefinitionVisibleSearchTerms,
    ),
    pageFriendlyName: accessibleItem.item.friendlyName,
    pageTitle,
    routeSearchTerms: getSettingsNavigationRouteSearchTerms([
      accessibleItem.item.href,
      href,
      ...getCanonicalRouteCandidates(href, routes),
    ]),
  };
}

function searchSettingsNavigationCandidates({
  candidates,
  query,
}: {
  candidates: readonly SettingsNavigationSearchCandidate[];
  query: string;
}): SettingsNavigationSearchCandidate[] {
  const searcher = new Fuse(candidates, {
    keys: [...SETTINGS_NAVIGATION_SEARCH_KEYS],
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 1,
    shouldSort: true,
    threshold: 0.35,
  });
  const candidatesByHref = new Map<
    string,
    { candidate: SettingsNavigationSearchCandidate; fuseScore: number }
  >();

  for (const queryVariant of getSettingsNavigationSearchQueryVariants(query)) {
    for (const result of searcher.search(queryVariant)) {
      const searchEvidence = getSettingSearchEvidence({
        groupLabel: result.item.groupTitle,
        groupValues: getSettingsNavigationCandidateGroupSearchValues(
          result.item,
        ),
        pageLabel: result.item.pageFriendlyName,
        pageValues: getSettingsNavigationCandidatePageSearchValues(result.item),
        search: query,
        settingLabels: result.item.matchedSettingLabels,
        settingValues: getSettingsNavigationCandidateSettingSearchValues(
          result.item,
        ),
      });
      if (!searchEvidence) {
        continue;
      }

      const existing = candidatesByHref.get(result.item.item.href);
      const fuseScore = result.score ?? 0;
      if (!existing || fuseScore < existing.fuseScore) {
        const candidate = addSearchEvidenceToSettingsNavigationCandidate({
          candidate: result.item,
          searchEvidence,
        });
        candidatesByHref.set(result.item.item.href, { candidate, fuseScore });
      }
    }
  }

  return [...candidatesByHref.values()]
    .sort((a, b) => {
      return (
        a.fuseScore - b.fuseScore ||
        b.candidate.catalogScore - a.candidate.catalogScore
      );
    })
    .map((result) => result.candidate);
}

function addSearchEvidenceToSettingsNavigationCandidate({
  candidate,
  searchEvidence,
}: {
  candidate: SettingsNavigationSearchCandidate;
  searchEvidence: SettingSearchEvidence;
}): SettingsNavigationSearchCandidate {
  return {
    ...candidate,
    item: {
      ...candidate.item,
      subtitle: getSettingsNavigationSearchEvidenceSubtitle({
        currentSubtitle: candidate.item.subtitle,
        searchEvidence,
      }),
    },
    searchEvidence,
  };
}

function getSettingsNavigationSearchEvidenceSubtitle({
  currentSubtitle,
  searchEvidence,
}: {
  currentSubtitle?: string;
  searchEvidence: SettingSearchEvidence;
}): string | undefined {
  switch (searchEvidence.kind) {
    case "page":
      return `Matched page: ${searchEvidence.label}`;
    case "group":
      return `Matched group: ${searchEvidence.label}`;
    case "setting":
      return currentSubtitle;
  }

  return currentSubtitle;
}

function getSettingsNavigationCandidatePageSearchValues(
  candidate: SettingsNavigationSearchCandidate,
): string[] {
  return [
    candidate.pageFriendlyName,
    candidate.pageTitle,
    ...candidate.routeSearchTerms,
  ];
}

function getSettingsNavigationCandidateGroupSearchValues(
  candidate: SettingsNavigationSearchCandidate,
): string[] {
  return [
    candidate.groupTitle,
    candidate.groupDescription,
    ...candidate.groupSearchTerms,
  ];
}

function getSettingsNavigationCandidateSettingSearchValues(
  candidate: SettingsNavigationSearchCandidate,
): string[] {
  return [
    ...candidate.matchedSettingDescriptions,
    ...candidate.matchedSettingLabels,
    ...candidate.matchedSettingSearchTerms,
  ];
}

function getSettingsNavigationSearchQueryVariants(query: string): string[] {
  return getUniqueStrings([
    query,
    ...query.split(/[-–—:;,.!?]/).map((part) => part.trim()),
    getSettingsNavigationKeywordQuery(query),
  ]);
}

function getSettingsNavigationKeywordQuery(query: string): string {
  return query
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(
      (token) =>
        token.length > 0 && !SETTINGS_NAVIGATION_SEARCH_STOP_WORDS.has(token),
    )
    .join(" ");
}

function getGroupVisibleSearchTerms(
  group: SettingGroupDefinition | undefined,
): string[] {
  if (!group) {
    return [];
  }

  return getUniqueStrings([
    group.title,
    group.description,
    ...(group.synonyms ?? []),
  ]);
}

function getCatalogDefinitionVisibleSearchTerms(
  definition: SettingDefinition,
): string[] {
  const metadata = definition.metadata;

  return getUniqueStrings([
    metadata.label,
    metadata.description,
    ...(metadata.synonyms ?? []),
    ...getCatalogDefinitionValueSemanticsSearchTerms(definition),
  ]);
}

function getSettingsNavigationRouteSearchTerms(
  hrefs: readonly string[],
): string[] {
  return getUniqueStrings(
    hrefs.flatMap((href) => {
      const terms = getRouteSearchTerms(
        canonicalizeSettingsNavigationHref(href),
      );
      return [terms.join(" "), ...terms];
    }),
  );
}

function getAccessibleSettingsNavigationItems(
  sections: readonly SettingsSection[],
  routes: SettingsNavigationSearchRoutes,
): AccessibleSettingsNavigationItem[] {
  return sections.flatMap((section) =>
    section.items.map((item) => ({
      canonicalHref: canonicalizeSettingsNavigationHref(item.href),
      item,
      sectionName: settingsSectionToFriendly[section.name],
    })),
  );
}

function findAccessibleSettingsNavigationItem({
  accessibleItems,
  href,
  routes,
}: {
  accessibleItems: readonly AccessibleSettingsNavigationItem[];
  href: string;
  routes: SettingsNavigationSearchRoutes;
}): AccessibleSettingsNavigationItem | undefined {
  const candidates = getCanonicalRouteCandidates(href, routes);

  for (const candidate of candidates) {
    const exactMatch = accessibleItems.find(
      (item) => item.canonicalHref === candidate,
    );
    if (exactMatch) {
      return exactMatch;
    }
  }

  return accessibleItems
    .filter((item) =>
      candidates.some((candidate) =>
        isCanonicalRouteOwnedBy(candidate, item.canonicalHref),
      ),
    )
    .sort((a, b) => b.canonicalHref.length - a.canonicalHref.length)[0];
}

function getCanonicalRouteCandidates(
  href: string,
  routes: SettingsNavigationSearchRoutes,
): string[] {
  const canonicalHref = canonicalizeSettingsNavigationHref(href);
  const legacyFaqHref = canonicalizeSettingsNavigationHref(
    `${routes.legacySupportBaseUrl}/faq`,
  );
  const settingsFaqHref = canonicalizeSettingsNavigationHref(
    routes.getSettingsSubRoute("faq"),
  );

  const candidates = [canonicalHref];
  if (isCanonicalRouteOwnedBy(canonicalHref, legacyFaqHref)) {
    candidates.push(settingsFaqHref);
  }
  if (canonicalHref === settingsFaqHref) {
    candidates.push(legacyFaqHref);
  }

  return getUniqueStrings(candidates);
}

function isCanonicalRouteOwnedBy(
  canonicalHref: string,
  ownerCanonicalHref: string,
): boolean {
  return (
    canonicalHref === ownerCanonicalHref ||
    canonicalHref.startsWith(`${ownerCanonicalHref}/`)
  );
}

function getVisibleDefinitionsForGroup({
  groupId,
  viewerVisibility,
}: {
  groupId: string;
  viewerVisibility: SettingVisibility;
}): readonly SettingDefinition[] {
  return getSettingCatalogDefinitionsForGroup(groupId).filter((definition) =>
    canReadSetting(viewerVisibility, definition.metadata.access),
  );
}

function getCatalogSearchTerms({
  definitions,
  group,
  href,
}: {
  definitions: readonly SettingDefinition[];
  group: SettingGroupDefinition;
  href: string;
}): string[] {
  return getUniqueStrings([
    group.id,
    group.title,
    group.description,
    ...(group.synonyms ?? []),
    href,
    ...getRouteSearchTerms(href),
    ...definitions.flatMap((definition) => [
      definition.id,
      definition.metadata.label,
      definition.metadata.description,
      ...(definition.metadata.synonyms ?? []),
      ...getCatalogDefinitionValueSemanticsSearchTerms(definition),
    ]),
  ]);
}

function getCatalogSearchHints({
  definitions,
  group,
}: {
  definitions: readonly SettingDefinition[];
  group: SettingGroupDefinition;
}): string[] {
  return getUniqueStrings([
    group.title,
    ...definitions.map((definition) => definition.metadata.label),
  ]);
}

function getCatalogSearchMatches({
  definitions,
  href,
}: {
  definitions: readonly SettingDefinition[];
  href: string;
}): SettingItemSearchMatch[] {
  return definitions.map((definition) => ({
    href: getSettingMatchHref({ href, settingId: definition.id }),
    label: definition.metadata.label,
    searchTerms: getCatalogDefinitionSearchTerms({ definition }),
    settingId: definition.id,
  }));
}

function getSettingMatchHref({
  href,
  settingId,
}: {
  href: string;
  settingId: string;
}): string {
  const hrefWithoutHash = href.split("#")[0] ?? href;
  return `${hrefWithoutHash}#${encodeURIComponent(settingId)}`;
}

function getCatalogDefinitionSearchTerms({
  definition,
}: {
  definition: SettingDefinition;
}): string[] {
  const metadata = definition.metadata;

  return getUniqueStrings([
    definition.id,
    ...getRouteSearchTerms(definition.id),
    metadata.label,
    metadata.description,
    ...(metadata.synonyms ?? []),
    ...getCatalogDefinitionValueSemanticsSearchTerms(definition),
  ]);
}

function getCatalogDefinitionValueSemanticsSearchTerms(
  definition: SettingDefinition,
): string[] {
  const valueSemantics = definition.metadata.valueSemantics;
  if (!valueSemantics) {
    return [];
  }

  return getUniqueStrings([
    valueSemantics.inputKind,
    valueSemantics.unit,
    valueSemantics.trueLabel,
    valueSemantics.falseLabel,
    valueSemantics.unsetLabel,
    ...(valueSemantics.options ?? [])
      .filter(isSearchableValueSemanticsOption)
      .flatMap((option) => [option.value, option.label, option.description]),
  ]);
}

function isSearchableValueSemanticsOption({
  label,
  value,
}: {
  label: string;
  value: string;
}): boolean {
  const optionTokens = [value, label].flatMap((term) =>
    getRouteSearchTerms(term).map((token) => token.toLowerCase()),
  );

  return !optionTokens.some((token) =>
    VALUE_SEMANTICS_OPTION_SEARCH_STOP_WORDS.has(token),
  );
}

function getRouteSearchTerms(href: string): string[] {
  return href
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[^a-zA-Z0-9]+/)
    .map((term) => term.trim())
    .filter((term) => term.length > 0);
}

function getOrCreateSearchTerms(
  itemSearchTerms: Map<SettingItem, Set<string>>,
  item: SettingItem,
): Set<string> {
  return getOrCreateSearchValues(itemSearchTerms, item);
}

function getOrCreateSearchMatches(
  itemSearchMatches: Map<SettingItem, SettingItemSearchMatch[]>,
  item: SettingItem,
): SettingItemSearchMatch[] {
  const existing = itemSearchMatches.get(item);
  if (existing) {
    return existing;
  }

  const created: SettingItemSearchMatch[] = [];
  itemSearchMatches.set(item, created);
  return created;
}

function addSearchMatch(
  matches: SettingItemSearchMatch[],
  match: SettingItemSearchMatch,
): void {
  if (
    matches.some(
      (existing) =>
        existing.label.toLowerCase() === match.label.toLowerCase() &&
        existing.href === match.href,
    )
  ) {
    return;
  }

  matches.push(match);
}

function getOrCreateSearchValues(
  itemSearchValues: Map<SettingItem, Set<string>>,
  item: SettingItem,
): Set<string> {
  const existing = itemSearchValues.get(item);
  if (existing) {
    return existing;
  }

  const created = new Set<string>();
  itemSearchValues.set(item, created);
  return created;
}

function addSearchValue(values: Set<string>, value: string | undefined): void {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return;
  }

  values.add(trimmedValue);
}

function getUniqueStrings(values: readonly (string | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
      continue;
    }

    const key = trimmedValue.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(trimmedValue);
  }

  return result;
}

function normalizePathname(pathname: string): string {
  if (pathname.length <= 1) {
    return pathname;
  }

  return pathname.replace(/\/+$/, "");
}
